/* the six small «what KI does» visuals – fictive data, rendered as real UI fragments (no screenshots) */
import { AlertCircle, ArrowRight, Check, FileText, Languages } from "lucide-react";

const amount = (n, unit = "kr") => `${Math.round(n).toLocaleString("nb-NO").replace(/\u00a0|\s/g, "\u202f")} ${unit}`;

const Src = ({ children }) => <span className="aix-src">{children}</span>;

const Rows = ({ d }) => (
  <div className="aix-rows">
    <div className="aix-rows-col">
      <div className="aix-rows-h">
        <FileText size={13} aria-hidden="true" /> {d.from.title} <span className="aix-rows-file">{d.from.file}</span>
      </div>
      <ol className="aix-raw">
        {d.from.rows.map(([cell, txt]) => (
          <li key={cell}>
            <span className="aix-raw-cell">{cell}</span>
            <span className="aix-raw-txt">{txt}</span>
          </li>
        ))}
      </ol>
    </div>
    <ArrowRight className="aix-rows-arrow" size={18} aria-hidden="true" />
    <div className="aix-rows-col">
      <div className="aix-rows-h">
        <Check size={13} aria-hidden="true" /> {d.to.title}
      </div>
      <table className="aix-table">
        <thead>
          <tr>
            {d.to.head.map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {d.to.rows.map((r) => (
            <tr key={r[0]} className={r[2] === "mangler" ? "is-gap" : ""}>
              <td>{r[0]}</td>
              <td>{r[1]}</td>
              <td>{r[2]}</td>
              <td>
                <Src>{r[3]}</Src>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const Fields = ({ d }) => (
  <div className="aix-fields">
    <div className="aix-fields-head">
      <span className="aix-tag">{d.tag}</span>
      <span className="aix-fields-name">{d.name}</span>
    </div>
    <dl className="aix-fields-grid">
      {d.fields.map(([k, v, src]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
          <Src>{src}</Src>
        </div>
      ))}
    </dl>
  </div>
);

const Gaps = ({ d }) => (
  <ol className="aix-gaps">
    {d.items.map(([tag, issue, q]) => (
      <li key={tag}>
        <AlertCircle size={16} aria-hidden="true" />
        <div>
          <div className="aix-gaps-top">
            <span className="aix-tag">{tag}</span>
            <span className="aix-gaps-issue">{issue}</span>
          </div>
          <p className="aix-gaps-q">«{q}»</p>
        </div>
      </li>
    ))}
  </ol>
);

const Proposal = ({ d }) => (
  <div className="aix-prop">
    <div className="aix-prop-card">
      <span className="aix-tag">{d.tag}</span>
      <div className="aix-prop-product">{d.product}</div>
      <div className="aix-prop-family">{d.family}</div>
      <ul className="aix-prop-opts">
        {d.opts.map((o) => (
          <li key={o}>{o}</li>
        ))}
      </ul>
    </div>
    <ArrowRight className="aix-rows-arrow" size={18} aria-hidden="true" />
    <div className="aix-prop-calc">
      <div className="aix-rows-h">{d.calcTitle}</div>
      <dl>
        {d.calc.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd className={k === "Status" ? "is-ok" : ""}>{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  </div>
);

const Draft = ({ d }) => (
  <div className="aix-draft">
    <div className="aix-draft-bar">
      <span className="aix-draft-label">{d.label}</span>
      <span className="aix-draft-langs">
        <Languages size={13} aria-hidden="true" />
        {d.langs.map((l, i) => (
          <span key={l} className={i === 0 ? "is-on" : ""}>
            {l}
          </span>
        ))}
      </span>
    </div>
    {d.lines.map((l) => (
      <p key={l}>{l}</p>
    ))}
  </div>
);

const Compare = ({ d }) => {
  const max = Math.max(...d.rows.flatMap((r) => [r[1], r[2]]));
  return (
    <div className="aix-cmp">
      <div className="aix-cmp-legend">
        {d.legend.map((l, i) => (
          <span key={l} className={`aix-cmp-key is-${i}`}>
            {l}
          </span>
        ))}
      </div>
      <ol>
        {d.rows.map(([k, a, b]) => {
          const diff = ((b - a) / a) * 100;
          return (
            <li key={k}>
              <span className="aix-cmp-k">{k}</span>
              <span className="aix-cmp-bars">
                <i className="is-0" style={{ "--w": `${(a / max) * 100}%` }} />
                <i className="is-1" style={{ "--w": `${(b / max) * 100}%` }} />
              </span>
              <span className="aix-cmp-v">
                {amount(b, d.unit)} <b className={diff > 0 ? "is-up" : "is-down"}>{`${diff > 0 ? "+" : "−"}${Math.abs(diff).toFixed(0)} %`}</b>
              </span>
            </li>
          );
        })}
      </ol>
      <p className="aix-cmp-suggest">{d.suggest}</p>
    </div>
  );
};

const KINDS = { rows: Rows, fields: Fields, gaps: Gaps, proposal: Proposal, draft: Draft, compare: Compare };

export const AiDemo = ({ demo }) => {
  const Cmp = KINDS[demo.kind];
  return Cmp ? <Cmp d={demo} /> : null;
};
